<template>
  <b-overlay :show="loading" opacity="0.5">
    <div>
      <list-table
        :records="users" 
        search-type="church"
        :columns="tableColumns"
        :total-records="totalRecords"
        :search-filter.sync="searchFilter"
        :use-auto-complete-search="true"
        :current-page-number.sync="currentPage"
        :show-export-button="can('church_profile:CanExport')"
        @row-clicked="onRowClicked"
        @export="onExportChurches"
      >
        <template #filters>
          <b-row>
            <b-col cols="12" md="2" class="">
              <v-select
                class="filter-select select-size-md"
                placeholder="Status"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :value="statusFilter"
                :options="statusOptions"
                :reduce="val => val.value"
                :disabled="loading"
                @input="(val) => statusFilter = val"
              />
            </b-col>
          </b-row>
        </template>

        <template #cell(owner)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar
                size="32"
                :src="getValueFromSource(data.item, 'owner.avatar.path')"
                :text="sentenceCase(avatarText(`${getValueFromSource(data.item, 'owner.full_name')}`))"
                :variant="`light-${resolveUserRoleVariant(getValueFromSource(data.item, 'owner.user_type'))}`"
                :to="{ name: 'admin-update-church', params: { id: getValueFromSource(data.item, 'owner.id') } }"
              />
            </template>
            <b-link
              :to="{ name: 'admin-update-church', params: { id: getValueFromSource(data.item, 'owner.id') } }"
              class="font-weight-bold d-block text-nowrap"
            >
              {{ sentenceCase(getValueFromSource(data.item, 'owner.full_name')) }}
            </b-link>
            <span v-if="getValueFromSource(data.item, 'owner.email')" class="text-muted">{{ getValueFromSource(data.item, 'owner.email') }}</span><br />
            <span v-if="getValueFromSource(data.item, 'owner.phone')" class="text-muted">{{ getValueFromSource(data.item, 'owner.phone') }}</span>
          </b-media>
        </template>


        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ getUserStatusText(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(meta)="data">
          <b-badge
            pill
            :variant="`light-${resolveStatusVariant(getValueFromSource(data.item, 'meta.church_approval'))}`"
            class="text-capitalize"
          >
            {{ getUserStatusText(getValueFromSource(data.item, 'meta.church_approval')) }}
          </b-badge>
        </template>
      </list-table>
    </div>
  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BAlert, BLink, BTable,
  BMedia, BAvatar, BButton, BFormFile,
  BBadge, BDropdown, BDropdownItem, BPagination,
  BInputGroup, BInputGroupAppend, BFormInput
} from 'bootstrap-vue'
import { get, debounce } from "lodash"

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

import FileDownload from "js-file-download";
import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const watchHandler = {
  handler(){
    this.debouncedFetchUsersData()
  }, 
  immediate: false
}
export default {
  components: {
    ListTable,

    BOverlay,
    BCard,
    BTable,
    BRow,
    BCol,
    BAlert,
    BBadge,
    BLink,
    BMedia,
    BAvatar,
    BDropdown,
    BDropdownItem,
    BPagination,
    BButton,
    BFormFile,
    BInputGroup, 
    BInputGroupAppend, 
    BFormInput,

    vSelect,
  },
  directives: {
    Ripple,
  },
  data(){
    return {
      loading: false,
      showFilter: false,
      showListTable: false,
      statusFilter: null,
      searchFilter: "",
      users: [],
      statusOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'Approved', value: 'approved' },
        { label: 'Declined', value: 'declined' },
      ],
      tableColumns: [
        { key: 'owner', stickyColumn: true, sortable: false, label: 'User' },
        { key: 'bank_account_name', sortable: false, label: 'Account Name', formatter: val => val || 'N/A' },
        { key: 'church_level', sortable: false, label: 'Payment Level', formatter: val => (val ? this.formatChurchLevel(val) : 'N/A') },
        { key: 'church_region', sortable: false, label: 'Region', formatter: val => val || 'N/A' },
        { key: 'church_area', sortable: false, label: 'Area', formatter: val => val || 'N/A' },
        { key: 'church_district', sortable: false, label: 'District', formatter: val => val || 'N/A' },
        { key: 'church_assembly', sortable: false, label: 'Assembly', formatter: val => val || 'N/A' },
        { key: 'status', sortable: false },
        { key: 'status', label: 'Approval', sortable: false },
        { key: 'created', label: 'Date', sortable: false, formatter: val => `${this.formatDate(val)}`, },
      ],
      debouncedFetchUsersData: () => {}
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
    dataMeta(){
      const localItemsCount = this.users.length || 0;
      return {
        from: this.perPage * (this.currentPage - 1) + (localItemsCount ? 1 : 0),
        to: this.perPage * (this.currentPage - 1) + localItemsCount,
        of: this.totalRecords,
      }
    }
  },
  watch: {
    perPage: {
      handler(){
        this.currentPage = 1;
        this.debouncedFetchUsersData()
      }, 
      immediate: false
    },
    currentPage: watchHandler,
    statusFilter: watchHandler,
    searchFilter: watchHandler,
  },
  created(){
    this.debouncedFetchUsersData = debounce(this.fetchAllData, 500);

    const { 
      status, 
      page = 1, 
      search = "", 
      limit = this.perPage 
    } = this.$route.query;

    this.currentPage = +page;
    this.searchFilter = search;
    this.statusFilter = status;
    this.perPage = +limit;
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          limit: this.perPage,
          page: this.currentPage,
          status: this.statusFilter,
          search: this.searchFilter,
        }

        this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().adminService.fetchChurches(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords
        this.perPage = pagination.limit;
        this.users = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
        this.showListTable = true;
      }
    },
    async onExportChurches(){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Export?',
          text: "This will export data based on current filters.",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        const query = {
          search: this.searchFilter,
          status: this.statusFilter
        }

        const response = await this.useJwt().adminService.exportChurches(query);
        FileDownload(response.data, "churches-export.csv");
      } catch (error) {
        const error_message = get(error, "response.statusText") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onRowClicked(rowItem){
      this.$router.push({ name: 'admin-update-church', params: { id: rowItem.owner._id } })
      .catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@core/scss/vue/libs/vue-select.scss';
  .width-100 {
    width: 100px;
  }
  .per-page-selector {
    width: 90px;
  }
</style>

<style lang="scss">
  .loan-list-tb-row {
    cursor: pointer !important;
  }
</style>
